import { config } from 'config/config'

// Status codes returned by the detail API when the ad no longer exists
// (sold/deactivated). Other errors (5xx, timeout, network) don't trigger
// a redirect — they keep the current behavior of showing the warning page.
export const INACTIVE_AD_STATUS_CODES = [404, 410]

const VEHICLE_TYPE_PATH = {
  car: 'carros',
  bike: 'motos',
}

function sanitizeSegment(segment) {
  return String(segment).trim().toLowerCase()
}

function getQueryString(originalUrl) {
  if (typeof originalUrl !== 'string') return ''
  const queryIndex = originalUrl.indexOf('?')
  return queryIndex >= 0 ? originalUrl.slice(queryIndex) : ''
}

/**
 * Same rule as getInactiveAdRedirectUrl, but taking detailState directly
 * instead of the full store state — usable from CSR (client-side route
 * transitions), where there's no declareSeoState gate to check because
 * fetchDeclareSeo('AdDetail') is only ever dispatched during SSR.
 *
 * The RB is served by search-frontend (webmotors.next.ui) and its URL
 * pattern differs by vehicle type:
 * - cars: /carros/estoque/{brand}/{model}/{version}
 * - bikes: /motos/estoque/{brand}/{model} — the 3rd segment on a bike DA
 *   is engine displacement (cc), not version; including it 404s on the RB.
 *
 * Returns null when it doesn't apply: ad is active (fetch succeeded), or
 * the API error has no inactive-ad status code.
 */
export function getInactiveAdRedirectUrlFromDetail(detail, originalUrl) {
  try {
    if (!detail || detail.fetched || !detail.requested) return null

    if (INACTIVE_AD_STATUS_CODES.indexOf(detail.statusCode) === -1) return null

    const { vehicleType, brand, model, version } = detail.requested
    if (!brand || !model) return null

    const vehiclePath = VEHICLE_TYPE_PATH[vehicleType] || VEHICLE_TYPE_PATH.car
    const versionSegment = vehicleType === 'bike' ? '' : version

    const segments = ['estoque', brand, model, versionSegment]
      .filter(Boolean)
      .map(sanitizeSegment)

    const baseUrl = String(config.urlApp || '/').replace(/\/+$/, '')

    return `${baseUrl}/${vehiclePath}/${segments.join('/')}${getQueryString(
      originalUrl
    )}`
  } catch (e) {
    // A failure building the redirect must never break the page —
    // falls back to the normal flow (warning page with 200).
    console.error('[inactiveAdRedirect] Failed to build redirect for inactive ad:', e)
    return null
  }
}

/**
 * When an ad detail page (DA) is inactive — the detail API responded with
 * 404/410 — returns the absolute search results (RB) URL derived from the
 * DA URL, so the server can respond with a single-hop 301.
 *
 * Returns null when it doesn't apply: page isn't a DA, ad is active (fetch
 * succeeded), or the API error has no inactive-ad status code.
 */
export function getInactiveAdRedirectUrl(state, originalUrl) {
  if (!state) return null

  const declareSeo = state.declareSeoState
  if (
    !declareSeo ||
    !declareSeo.result ||
    declareSeo.result.container !== 'AdDetail'
  )
    return null

  return getInactiveAdRedirectUrlFromDetail(state.detailState, originalUrl)
}
